import React, { useCallback, useRef, useState } from "react";
import { api, formatApiErr } from "@/lib/api";
import { Upload, X, RefreshCw, Image as ImageIcon } from "lucide-react";

/**
 * Polished logo upload with drag-and-drop, preview, replace, remove, skip.
 * Server enforces mime allow-list + size; component surfaces server errors.
 */
const ACCEPT = "image/png,image/svg+xml,image/jpeg,image/webp";
const ACCEPT_HUMAN = "PNG or SVG (max 10MB)";
const MAX_BYTES = 10 * 1024 * 1024;

export default function LogoUpload({ value, onChange, onSkip }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fileName, setFileName] = useState("");

  const upload = useCallback(async (file) => {
    if (!file) return;
    setErr(""); setFileName(file.name);
    if (file.size > MAX_BYTES) { setErr("File too large. Max 10MB."); return; }
    if (!/^image\/(png|svg\+xml|jpeg|webp)$/.test(file.type)) {
      setErr("Unsupported format. Use PNG or SVG.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd);
      const url = process.env.REACT_APP_BACKEND_URL + data.url;
      onChange && onChange(url);
    } catch (e) {
      setErr(formatApiErr(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  }, [onChange]);

  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
  };
  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  if (value) {
    return (
      <div className="border-2 border-zinc-200 rounded-xl p-5 flex items-center gap-4" data-testid="logo-preview">
        <img src={value} alt="Logo" className="w-20 h-20 object-contain rounded-lg border bg-white"/>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Logo uploaded</div>
          <div className="text-xs text-zinc-500 truncate">{fileName || "current-logo"}</div>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => inputRef.current?.click()} className="text-sm border border-zinc-200 hover:bg-zinc-50 rounded-md px-3 py-1.5 flex items-center gap-1" data-testid="logo-replace">
            <RefreshCw className="w-3.5 h-3.5"/> Replace
          </button>
          <button type="button" onClick={() => { onChange && onChange(""); setFileName(""); }} className="text-sm text-red-600 hover:bg-red-50 rounded-md px-3 py-1.5 flex items-center gap-1" data-testid="logo-remove">
            <X className="w-3.5 h-3.5"/> Remove
          </button>
        </div>
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={onFileInput}/>
      </div>
    );
  }

  return (
    <>
      <label
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`block cursor-pointer border-2 border-dashed rounded-xl p-8 text-center transition ${drag ? "border-[var(--brand-accent)] bg-orange-50" : "border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50"}`}
        data-testid="logo-dropzone"
      >
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={onFileInput} data-testid="logo-input"/>
        <div className="w-12 h-12 rounded-full bg-[var(--brand-accent-soft,#F7E6DD)] mx-auto flex items-center justify-center mb-3">
          <Upload className="w-5 h-5 text-[var(--brand-accent)]"/>
        </div>
        <div className="text-sm font-medium">Upload company logo</div>
        <div className="text-xs text-zinc-500 mt-1">Drag &amp; drop or click to browse · {ACCEPT_HUMAN}</div>
        <button type="button" onClick={(e) => { e.preventDefault(); inputRef.current?.click(); }}
          className="mt-4 bg-[var(--brand-primary)] text-white text-sm rounded-md px-4 py-2 hover:bg-black inline-flex items-center gap-2"
          data-testid="logo-choose-btn"
          disabled={busy}>
          <ImageIcon className="w-4 h-4"/> {busy ? "Uploading…" : "Choose file"}
        </button>
        {fileName && !err && <div className="text-xs text-zinc-500 mt-2">{fileName}</div>}
        {err && <div className="text-xs text-red-600 mt-2" role="alert">{err}</div>}
      </label>
      {onSkip && (
        <button type="button" onClick={onSkip} className="mt-3 text-sm text-zinc-500 hover:text-zinc-800" data-testid="logo-skip">
          Skip for now →
        </button>
      )}
    </>
  );
}
